import { useContext, createRef, useState } from "react"
import { RichText } from "prismic-reactjs"
import cc from "classcat"
import SEO from "components/seo"
import useTranslations from "hooks/useTranslations"
import { LocaleContext } from "contexts"
import VisibilitySensor from "react-visibility-sensor"

const LegalTemplate = ({ pageContext: { content } }) => {
  const [sectionsState, setSectionsState] = useState({})
  const { legalLastUpdated } = useTranslations()
  const { locale } = useContext(LocaleContext)
  const documentTitle = content.document_title.text
  const lastUpdated = new Date(content.last_updated)
  const formatLastUpdated = new Intl.DateTimeFormat(locale).format(lastUpdated)

  const refs = content.body.reduce((acc, section) => {
    acc[section.id] = createRef()
    return acc
  }, {})

  const goToSection = (id) => {
    refs[id].current.scrollIntoView({
      behavior: "smooth",
      block: "start",
    })
  }

  const LegalDocSection = ({ body, title, id }) => {
    return (
      <VisibilitySensor
        partialVisibility
        onChange={(isVisible) =>
          sectionsState[id] !== isVisible &&
          setSectionsState((prev) => ({ ...prev, [id]: isVisible }))
        }
        minTopValue={200}
      >
        <section className="Section">
          <div className="h-px w-full" ref={refs[id]} />
          <h2>{title}</h2>
          <RichText render={body} />
        </section>
      </VisibilitySensor>
    )
  }

  return (
    <>
      <SEO title={documentTitle} />
      <div className="relative mt-16 w-full bg-white px-4 py-16 sm:max-w-3xl sm:px-6 lg:max-w-7xl lg:px-8 xl:mx-auto">
        <div className="prose max-w-screen-lg text-lg">
          <h1 className="">{documentTitle}</h1>
        </div>
        {content.last_updated ? (
          <div className="mt-4">
            <span className="text-sm">{legalLastUpdated}: </span>
            <span className="text-sm">{formatLastUpdated}</span>
          </div>
        ) : null}
        <div className="flex flex-row justify-start md:space-x-10">
          <div className="sticky top-0 hidden h-full w-56 flex-col pt-8 text-left md:flex">
            {content.body.map((section) => {
              return (
                // eslint-disable-next-line jsx-a11y/click-events-have-key-events
                <h4
                  // eslint-disable-next-line jsx-a11y/no-noninteractive-element-to-interactive-role
                  role="link"
                  onClick={() => goToSection(section.id)}
                  className={cc([
                    "mb-3 cursor-pointer text-sm leading-4",
                    sectionsState[section.id] === true
                      ? "text-kkm-darkgreen"
                      : "text-zinc-400",
                  ])}
                >
                  {section.primary.document_section_title.text}
                </h4>
              )
            })}
          </div>
          <div className="prose prose-sm prose-teal mx-auto w-full max-w-prose text-sm">
            {content.document_intro ? (
              <div className="lead md:pt-4">
                <RichText render={content.document_intro.richText} />
              </div>
            ) : null}
            {content.body.map((section) => {
              const sectionTitle = section.primary.document_section_title.text
              return (
                <LegalDocSection
                  title={sectionTitle}
                  body={section.items[0].document_section_text.richText}
                  key={section.id}
                  id={section.id}
                />
              )
            })}
          </div>
        </div>
      </div>
    </>
  )
}

export default LegalTemplate
